// src/lib/callables.js
//
// Thin client wrappers around the WP-1/WP-2 Cloud Functions (httpsCallable).
// Every wrapper hard-fails in mock mode (P7 invariant: mock does NO backend work);
// callers MUST branch on AUTH_MODE before invoking. Each returns the CF's `.data`.
import { httpsCallable } from 'firebase/functions';
import { functions, AUTH_MODE } from '../auth/firebase.js';
import { observeBetaOperation } from '../analytics/betaOperations.js';
export async function adminBlog(data) { return (await callable('adminBlog')(data)).data; }

function callable(name) {
    if (AUTH_MODE === 'mock' || !functions) {
        throw new Error(
            `Cloud Functions are unavailable in AUTH_MODE="${AUTH_MODE}". ` +
            `"${name}" requires emulator or firebase mode.`
        );
    }
    return httpsCallable(functions, name);
}

export async function updateNotificationSettings(data) {
    return (await callable('onAdminUpdateNotificationSettings')(data)).data;
}
export async function sendNotificationTest() {
    return (await callable('onAdminSendNotificationTest')({})).data;
}

// WP-1: returns { sessionId, url, checkoutAttemptId, totalAmount, currency }
export async function createServiceCheckout(data) {
    return (await callable('createServiceCheckout')(data)).data;
}
export async function redeemAccessCode(data) {
    return (await callable('redeemAccessCode')(data)).data;
}
export async function adminBookingInvitation(data) { return (await callable('adminBookingInvitation')(data)).data; }
export async function redeemBookingInvitation(data) { return (await callable('redeemBookingInvitation')(data)).data; }
export async function proposeBookingReschedule(data) { return (await callable('proposeBookingReschedule')(data)).data; }
export async function adminReviewBookingReschedule(data) { return (await callable('adminReviewBookingReschedule')(data)).data; }
export async function createStripeCheckoutSession(data) {
    const res = await callable('createStripeCheckoutSession')(data);
    return res.data;
}

// WP-2: returns { pendingId, totalAmount, currency, status }
export async function submitPendingPurchase(data) {
    const res = await callable('submitPendingPurchase')(data);
    return res.data;
}

// WP-2: returns { consentIds, count }
export async function recordLegalConsent(data) {
    const res = await callable('recordLegalConsent')(data);
    return res.data;
}

// ---- Promo codes (promo-codes slice) ----
// Dry-run price preview: { ok, items, totalAmount, originalTotal, discountTotal,
// currency, minorUnits, appliedCode, discountPercent } or { ok: false, reason }.
export async function previewPurchase(data) {
    return (await callable('previewPurchase')(data)).data;
}
export async function adminUpsertPromoCode(data) {
    return (await callable('adminUpsertPromoCode')(data)).data;
}
export async function adminSetPromoCodeEnabled(data) {
    return (await callable('adminSetPromoCodeEnabled')(data)).data;
}

// ---- WP-4 admin pack/content management (admin-gated server-side) ----
const draftPacks = new Set();
export async function explainPassage(data) { return observeBetaOperation('explain', data, async () => (await callable('explainPassage')(data)).data); }
export async function adminSaveSchoolLicense(data) { return (await callable('adminSaveSchoolLicense')(data)).data; }
export async function inviteSchoolMembers(data) { return (await callable('inviteSchoolMembers')(data)).data; }
export async function acceptSchoolInvitation(data) { return (await callable('acceptSchoolInvitation')(data)).data; }
export async function updateSchoolMember(data) { return (await callable('updateSchoolMember')(data)).data; }
export async function getSchoolOverview(data = {}) { return (await callable('getSchoolOverview')(data)).data; }
export async function getMemberLearningReport(data) { return (await callable('getMemberLearningReport')(data)).data; }
export function setContentDraftMode(packId, enabled) { if (enabled) draftPacks.add(packId); else draftPacks.delete(packId); }
const draftInput = data => ({ ...data, draft: draftPacks.has(data?.packId) });
export async function beginContentDraft(data) { return (await callable('beginContentDraft')(data)).data; }
export async function createContentDraftPack(data) { return (await callable('createContentDraftPack')(data)).data; }
export async function reviewContentDraftAction(data) { return (await callable('reviewContentDraftAction')(data)).data; }
export async function commentContentDraft(data) { return (await callable('commentContentDraft')(data)).data; }
export async function adminPackCreate(data) {
    return (await callable('adminPackCreate')(data)).data;
}
export async function adminPackUpdate(data) {
    return (await callable('adminPackUpdate')(data)).data;
}
export async function adminItemCreate(data) {
    return (await callable('adminItemCreate')(draftInput(data))).data;
}
export async function adminItemUpdate(data) {
    return (await callable('adminItemUpdate')(draftInput(data))).data;
}
export async function adminItemDelete(data) {
    return (await callable('adminItemDelete')(draftInput(data))).data;
}
// Move an item between freeItems <-> paidItems (keeps the id). { packId, itemId,
// fromScope, toScope } -> { packId, itemId, fromScope, toScope, moved }.
export async function adminItemMove(data) {
    return (await callable('adminItemMove')(draftInput(data))).data;
}
// Swap two items' displayOrder (section reorder; metadata only, ids-only contract).
// { packId, moved: {scope,itemId}, neighbour: {scope,itemId}, direction: 'up'|'down' }
export async function adminItemReorder(data) {
    return (await callable('adminItemReorder')(draftInput(data))).data;
}
// Delete a pack + content subcollections (warn-and-allow). { packId } ->
// { packId, deleted, freeItems, paidItems }. Entitlements/transactions kept.
export async function adminPackDelete(data) {
    return (await callable('adminPackDelete')(data)).data;
}
// Active-entitlement count for the delete-confirm warning.
// { packId } -> { packId, activeEntitlements, totalEntitlements }.
export async function adminPackEntitlementCount(data) {
    return (await callable('adminPackEntitlementCount')(data)).data;
}
// Slice L2a-1: set/clear a pack's AI grading rubric + grading config (admin-read
// subdoc). { packId, rubric, configJson? } -> { packId, aiGradingEnabled,
// gradingConfigSet? }. configJson: JSON string (validated server-side), ''/null
// clears, undefined leaves the stored config untouched.
export async function onAdminPackSourceUpdate(data) {
    return (await callable('onAdminPackSourceUpdate')(data)).data;
}

export async function onAdminPackGradingUpdate(data) {
    return (await callable('onAdminPackGradingUpdate')(data)).data;
}
export async function getUploadUrl(data) {
    return (await callable('getUploadUrl')(data)).data;
}
// Paid-media read: pass ONLY { packId, itemId }; server derives the path +
// checks entitlement. Returns { url, storagePath, expiresAt }.
export async function getSignedUrlForItem(data) {
    return (await callable('getSignedUrlForItem')(draftInput(data))).data;
}

// ---- WP-5 admin: manual approve (WP-2 CF) / reject, legal publish, user notes ----
export async function onManualApprovePurchase(data) {
    return (await callable('onManualApprovePurchase')(data)).data;
}
export async function onManualRejectPurchase(data) {
    return (await callable('onManualRejectPurchase')(data)).data;
}
export async function onAdminPublishLegalDocument(data) {
    return (await callable('onAdminPublishLegalDocument')(data)).data;
}
export async function onAdminSetUserNote(data) {
    return (await callable('onAdminSetUserNote')(data)).data;
}
// Members entitlement grant/revoke (admin-gated). grant: { uid, packId, term } ->
// { uid, packId, term, status, expiresAt }. revoke: { uid, packId, reason? } ->
// { uid, packId, status }.
export async function adminGrantEntitlement(data) {
    return (await callable('adminGrantEntitlement')(data)).data;
}
export async function adminRevokeEntitlement(data) {
    return (await callable('adminRevokeEntitlement')(data)).data;
}
// Service-request lifecycle (Slice A; admin-gated, off-platform payment).
export async function adminQuoteServiceRequest(data) {
    return (await callable('adminQuoteServiceRequest')(data)).data;
}
export async function adminMarkServiceRequestPaid(data) {
    return (await callable('adminMarkServiceRequestPaid')(data)).data;
}
export async function adminCompleteServiceRequest(data) {
    return (await callable('adminCompleteServiceRequest')(data)).data;
}
export async function adminRejectServiceRequest(data) {
    return (await callable('adminRejectServiceRequest')(data)).data;
}
export async function adminArchiveServiceRequest(data) {
    return (await callable('adminArchiveServiceRequest')(data)).data;
}
// ---- WP-5 Slice 2: editable copy + config ----
export async function onAdminSiteContentUpdate(data) {
    return (await callable('onAdminSiteContentUpdate')(data)).data;
}
export async function onAdminConfigUpdate(data) {
    return (await callable('onAdminConfigUpdate')(data)).data;
}

// ---- WP-6: AI Study Partner chat ----
// Pass { packId, message, history?, depth? }. Returns
// { answer, model, entitled, disclaimer, contextTokens, usage, quotaEventId }.
export async function onAiChat(data) {
    try { return await observeBetaOperation('study_partner', data, async () => (await callable('onAiChat')(data)).data); } finally { window.dispatchEvent(new Event('btl-ai-usage')); }
}
// WP-6 admin kill switch: { aiDisabled, disabledReason? } -> { aiDisabled }.
export async function onAdminSetAiRuntime(data) {
    return (await callable('onAdminSetAiRuntime')(data)).data;
}

// ---- Slice L1: AI essay grading ----
// Send { packId, promptId, essayText }. Returns either
//   { status:'graded', ao4Mark, ao4Level, ao5Mark, ao5Level, totalMark,
//     indicativeBandRange, mostLikelyBand, profileNote?, confidence, criteria,
//     improvements, disclaimer }
// (marks/total/grade are SERVER-computed; the model only judges AO quality), or
// { status:'unavailable', reason } (not_entitled | not_configured | oversized |
// rate_limited | disabled | malformed_model_output | provider_error).
export async function gradeEssay(data) {
    try { return await observeBetaOperation('grade_pack', data, async () => (await callable('gradeEssay')(data)).data); } finally { window.dispatchEvent(new Event('btl-ai-usage')); }
}

// ---- Public pack free-preview (pack-free-preview slice; NO auth required) ----
// Send { packId }. Returns { status:'ok', pack:{id,title,packType,theme,spineLabel},
// items:[{id,type,title,displayOrder,content}] } with FREE items only, or
// { status:'not_available' } for hidden/draft/missing packs.
export async function getPackPreview(data) {
    return (await callable('getPackPreview')(data)).data;
}

// ---- Own-essay AI grading (own-essay-ai-grading slice; auth + entitlement) ----
// Send { rubricPackId, promptText, essayText }. Same graded shape as gradeEssay
// plus remainingToday (shared daily grade budget left), or
// { status:'unavailable', reason } with the existing FAILURE_MESSAGES reasons.
export async function gradeOwnEssay(data) {
    try { return await observeBetaOperation('grade_own', data, async () => (await callable('gradeOwnEssay')(data)).data); } finally { window.dispatchEvent(new Event('btl-ai-usage')); }
}
// Rubric picker: { packs: [{ packId, label }] } -- the caller's ACTIVE
// entitled packs that have AI grading enabled. Never returns rubric text.
export async function listGradableEntitledPacks(data) {
    return (await callable('listGradableEntitledPacks')(data || {})).data;
}

export async function activateGradingTrial(data) {
    return (await callable('activateGradingTrial')(data)).data;
}
export async function previewPackOffer(data) { return (await callable('previewPackOffer')(data)).data; }
export async function createPackOffer(data) { return (await callable('createPackOffer')(data)).data; }

export async function updateStaffAccess(data) { return (await callable('updateStaffAccess')(data)).data; }

export async function removeStaffAccess(data) { return (await callable('removeStaffAccess')(data)).data; }
export async function requestPackDeletion(data) { return (await callable('requestPackDeletion')(data)).data; }
export async function dismissPackDeletion(data) { return (await callable('dismissPackDeletion')(data)).data; }

export async function aiUsageSummary(data) { return (await callable('aiUsageSummary')(data)).data; }

export async function uploadSiteImage(data) { return (await callable('uploadSiteImage')(data)).data; }

export async function adminRemovePromoCode(data) { return (await callable('adminRemovePromoCode')(data)).data; }

export async function staffMessagesAction(data) { return (await callable('staffMessagesAction')(data)).data; }

export async function schoolFeedbackAction(data) { return (await callable('schoolFeedbackAction')(data)).data; }

export async function saveSchoolEssayAttempt(data) { return (await callable('saveSchoolEssayAttempt')(data)).data; }
export async function learningNotificationsAction(data) { return (await callable('learningNotificationsAction')(data)).data; }

export async function bookingMessagesAction(data) { return (await callable('bookingMessagesAction')(data)).data; }
// Teacher's Pet uses authenticated callables; mock mode never contacts the backend.
export async function teachersPet(data) { return (await callable('teachersPet')(data)).data; }
export async function teachersPetFocus(data) { return (await callable('teachersPetFocus')(data)).data; }
export async function teachersPetPractice(data) { return (await callable('teachersPetPractice')(data)).data; }
export async function teachersPetAdmin(data) { return (await callable('teachersPetAdmin')(data)).data; }
export async function teachersPetCatalog() { return (await callable('teachersPetCatalog')({})).data; }
export async function teachersPetContentAdmin(data) { return (await callable('teachersPetContentAdmin')(data)).data; }
